import React from 'react';
import { time } from '~/utils';
import { makeStyles } from '@material-ui/core/styles';
import PersonIcon from '@material-ui/icons/Person';
import { colors, SvgIcon } from '@material-ui/core';
import { Changelog as ChangelogType } from '~/types/Changelog';
import cx from 'classnames';
import { getTypeOfIdentity } from '~/utils/identities';
import { useTranslate, usePermissions, Button } from 'react-admin';
import palette from '~/theme/palette';
import ReferenceField from './ReferenceField';
import { ArrowForwardCircleIcon } from '~/img';
import { useReferenceEntity } from '~/hooks';
import { isEmpty } from 'lodash';

interface Props {
  changelogId: number;
  changelogData: ChangelogType;
  handleTurnOnChangelogMode: (id: number) => void;
  hasChangelogPermissionView: boolean;
  selectedChangelog: number | undefined;
}

const useStyles = makeStyles((theme) => {
  return {
    gridContainer: {
      display: 'flex',
      padding: '15px 10px',
      borderRadius: '5px',
      backgroundColor: colors.grey[200],
      marginBottom: '10px',
    },
    pointer: {
      transition: `${theme.transitions.easing.easeIn}`,
      transitionDuration: `${theme.transitions.duration.shortest}ms`,
      '&:hover': {
        cursor: 'pointer',
        backgroundColor: colors.grey[100],
      },
    },
    yellow: {
      backgroundColor: palette.changelog.base,
      '&:hover': {
        backgroundColor: palette.changelog.baseDarker,
      },
    },
    root: {
      display: 'flex',
      width: '100%',
      flexGrow: 1,
      alignItems: 'center',
    },
    avatar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: '10px',
      alignSelf: 'flex-start',
    },
    icon: {
      backgroundColor: colors.grey[200],
      borderRadius: '50%',
      border: `10px solid ${colors.grey[600]}`,
      color: colors.grey[600],
      width: '20px',
      height: '20px',
    },
    identities: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      flexGrow: 1,
    },
    viewCaseButton: {
      alignSelf: 'flex-end',
    },
  };
});

const Changelog = (props: Props) => {
  const {
    changelogId,
    changelogData,
    handleTurnOnChangelogMode,
    hasChangelogPermissionView,
    selectedChangelog,
  } = props;
  const classes = useStyles();
  const translate = useTranslate();
  const label = (key: string, opts?: any) =>
    translate(`components.OperationTypeChangelog.${key}`, opts);
  const { permissions } = usePermissions();
  const hasApprovalCaseView =
    permissions?.includes('approvalCase.view') || permissions?.includes('approvalCase.admin');
  const originatedById: string = changelogData?.originatorIdentity?.id ?? '';
  const originatedByType: string = getTypeOfIdentity(
    changelogData?.originatorIdentity?.type ?? 'SYSTEM'
  );
  const record = { id: originatedById, type: originatedByType };

  const approvalCaseId = changelogData?.approvalCaseId;
  const isApprovalCase = !!approvalCaseId;
  const { data: approvalCaseEntity, isLoading: isApprovalCaseLoading } = useReferenceEntity(
    { id: approvalCaseId!, type: 'APPROVAL_CASE' },
    isApprovalCase && hasApprovalCaseView
  );
  const submittedAt = approvalCaseEntity?.submittedAt;

  const decidedByRecords: { id: string; type: string; decidedAt?: string }[] = (() => {
    if (!approvalCaseEntity?.steps) return [];
    return (approvalCaseEntity.steps as any[])
      .filter((s) => s.decidedByIdentityId && s.decidedByIdentityType)
      .map((s) => ({
        id: s.decidedByIdentityId,
        type: getTypeOfIdentity(s.decidedByIdentityType),
        decidedAt: s.decidedAt ?? undefined,
      }));
  })();

  return (
    <div>
      <div
        className={cx(
          classes.gridContainer,
          {
            [classes.yellow]: changelogId === selectedChangelog,
          },
          hasChangelogPermissionView && classes.pointer
        )}
        onClick={() => (hasChangelogPermissionView ? handleTurnOnChangelogMode(changelogId) : null)}
      >
        <div className={classes.root}>
          <div className={classes.avatar}>
            <PersonIcon className={classes.icon} />
          </div>
          <div className={classes.identities}>
            <ReferenceField
              label={
                isApprovalCaseLoading
                  ? '\u00A0'
                  : !isApprovalCase || !submittedAt
                  ? `${label(changelogData?.operation)} ${dateFormat(changelogData?.timestamp)}`
                  : label(`requestedOn.${changelogData?.operation}`, {
                      date: dateFormat(submittedAt),
                    })
              }
              record={record}
            />

            {isApprovalCase && !isEmpty(decidedByRecords) && (
              <>
                {decidedByRecords.map((rec) => (
                  <ReferenceField
                    key={rec.id}
                    label={label('approvedOn', {
                      date: rec.decidedAt ? dateFormat(rec.decidedAt) : '',
                    })}
                    record={rec}
                  />
                ))}
              </>
            )}
            {isApprovalCase && hasApprovalCaseView && (
              <Button
                className={classes.viewCaseButton}
                label={label('viewApprovalCase')}
                href={`/approvals/history/${approvalCaseId}/show`}
                target='_blank'
                onClick={(e: any) => e.stopPropagation()}
              >
                <SvgIcon>
                  <ArrowForwardCircleIcon />
                </SvgIcon>
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const dateFormat = (dateString: string) => {
  return `${time(dateString).format('l')} ${time(dateString).format('LT')}`;
};

export default Changelog;
